import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { CustomViewEntity, useCustomViews } from 'src/graphql/entities/custom-view';
import { useSelectedRecord } from 'src/graphql/entities/search/use-selected-record';
import { EntityNames } from 'src/graphql/types';
import { MAP_ENTITY_TO_URL_ENTITY } from 'src/pages/CustomViews/constants';
import { useGlobalStore } from 'src/store';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { useSelectedViewUrlContext } from './use-selected-view-url-context';

export const useSelectedEntityParams = ({ entityName }: { entityName: CustomViewEntity }) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { recordId, selectedCustomViewId, isStaticView, viewId } =
    useSelectedViewUrlContext(entityName);

  const { navigate } = useNavigateRoute();

  const { customViews } = useCustomViews({
    entity: entityName,
    partnerId: selectedPartnerId,
  });

  const customView = useMemo(
    () => customViews.find((i) => i.customViewId === selectedCustomViewId),
    [customViews, selectedCustomViewId],
  );

  const { selectedRecord: record } = useSelectedRecord({
    recordId,
    entityName: entityName as EntityNames,
  });

  const closeRecord = useCallback(() => {
    const entity = MAP_ENTITY_TO_URL_ENTITY[entityName] ?? entityName;

    const url = isStaticView
      ? portalPaths.entity.all({
          entityName: entity,
        })
      : portalPaths.entity.selected(entity, selectedCustomViewId);

    navigate(url);
  }, [navigate, isStaticView, selectedCustomViewId, entityName]);

  const openRecord = useCallback(
    (id: string) => {
      const entity = MAP_ENTITY_TO_URL_ENTITY[entityName] ?? entityName;

      const url = isStaticView
        ? portalPaths.entity.all({
            entityName: entity,
            id,
          })
        : portalPaths.entity.selected(entity, selectedCustomViewId, id);

      navigate(url);
    },
    [navigate, isStaticView, selectedCustomViewId, entityName],
  );

  return {
    recordId,
    record,
    selectedCustomViewId,
    viewId,
    customView,
    closeRecord,
    openRecord,
  };
};
